import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowUpDown } from "lucide-react";
import {
  CARNIVAL,
  MARQUEE_EVENTS,
  CARNIVAL_LAST_UPDATED,
} from "../../lib/carnivalMeta";

const daysUntil = (isoDate, today) => {
  if (!isoDate) return null;
  const target = new Date(`${isoDate}T00:00:00-04:00`);
  const now = new Date(`${today}T00:00:00-04:00`);
  const ms = target.getTime() - now.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const daysBadgeStyle = (days, status) => {
  if (days === null) {
    return "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]";
  }
  if (days < 0) {
    return "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]";
  }
  if (status === "confirmed") {
    return "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]";
  }
  if (days <= 30) {
    return "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]";
  }
  if (days <= 90) {
    return "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]";
  }
  return "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]";
};

const statusBadgeStyle = (status) => {
  if (status === "confirmed") {
    return "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]";
  }
  return "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]";
};

const buildRows = () => {
  const today = new Date().toISOString().slice(0, 10);
  const rows = Object.entries(MARQUEE_EVENTS).map(([slug, marquee]) => {
    const island = CARNIVAL[marquee.islandKey];
    const event = island?.events?.find((e) => e.key === marquee.eventKey);
    const date = event?.date || null;
    const status = event?.status || "unknown";
    const time = event?.time_local || null;
    const label = event?.labels?.en || marquee.eventKey;
    const days = daysUntil(date, today);
    return {
      slug,
      islandKey: marquee.islandKey,
      islandName: island?.name || marquee.islandKey,
      eventKey: marquee.eventKey,
      eventLabel: label,
      date,
      time,
      status,
      days,
      dateModified: event?.dateModified || CARNIVAL_LAST_UPDATED,
      dateModifiedIsPerEvent: !!event?.dateModified,
      metaTitle: marquee.meta?.en?.title || "",
    };
  });
  return { rows, today };
};

export const MarqueeFreshnessTab = () => {
  const [sortKey, setSortKey] = useState("days");
  const [sortDir, setSortDir] = useState("asc");
  const { rows, today } = useMemo(buildRows, []);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (av === null || av === undefined) av = "";
      if (bv === null || bv === undefined) bv = "";
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const s = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? s : -s;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  const totalCount = sorted.length;
  const confirmedCount = sorted.filter((r) => r.status === "confirmed").length;
  const estimatedCount = sorted.filter((r) => r.status === "estimated").length;
  const soonCount = sorted.filter(
    (r) => r.days !== null && r.days >= 0 && r.days <= 30
  ).length;

  return (
    <div className="space-y-6" data-testid="marquee-freshness-tab">
      <header className="space-y-2">
        <h2 className="font-serif-display text-2xl text-[#1A1A1A]">
          Marquee Freshness
        </h2>
        <p className="text-sm text-[#6B7280] max-w-3xl">
          One row per standalone marquee event page. Use this to decide which
          pages need a copy refresh when SCDF/SMAC/FDKK/BCC publish their firm
          2027 schedules (typically Oct–Dec 2026). Sorted by days-until-event
          by default; click a header to re-sort.
        </p>
        <p className="text-xs text-[#6B7280]" data-testid="freshness-today">
          Reference date: {today} · Data source: /app/frontend/src/lib/carnivalMeta.js
        </p>
      </header>

      <section
        className="grid grid-cols-2 md:grid-cols-4 gap-3"
        data-testid="freshness-summary"
      >
        <div className="border border-[#E5E7EB] rounded-md p-3">
          <div className="text-xs uppercase tracking-wider text-[#6B7280]">
            Total marquees
          </div>
          <div
            className="text-2xl font-serif-display text-[#1A1A1A]"
            data-testid="freshness-total"
          >
            {totalCount}
          </div>
        </div>
        <div className="border border-[#E5E7EB] rounded-md p-3">
          <div className="text-xs uppercase tracking-wider text-[#6B7280]">
            Confirmed
          </div>
          <div
            className="text-2xl font-serif-display text-[#065F46]"
            data-testid="freshness-confirmed"
          >
            {confirmedCount}
          </div>
        </div>
        <div className="border border-[#E5E7EB] rounded-md p-3">
          <div className="text-xs uppercase tracking-wider text-[#6B7280]">
            Estimated
          </div>
          <div
            className="text-2xl font-serif-display text-[#991B1B]"
            data-testid="freshness-estimated"
          >
            {estimatedCount}
          </div>
        </div>
        <div className="border border-[#E5E7EB] rounded-md p-3">
          <div className="text-xs uppercase tracking-wider text-[#6B7280]">
            Event within 30d
          </div>
          <div
            className="text-2xl font-serif-display text-[#92400E]"
            data-testid="freshness-soon"
          >
            {soonCount}
          </div>
        </div>
      </section>

      <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="marquee-freshness-table">
          <thead className="bg-[#F9FAFB] text-left text-xs uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-3 py-2">
                <button
                  className="inline-flex items-center gap-1 hover:text-[#1A1A1A]"
                  onClick={() => setSort("slug")}
                  data-testid="sort-slug"
                >
                  Slug <ArrowUpDown size={12} />
                </button>
              </th>
              <th className="px-3 py-2">
                <button
                  className="inline-flex items-center gap-1 hover:text-[#1A1A1A]"
                  onClick={() => setSort("islandName")}
                  data-testid="sort-island"
                >
                  Island <ArrowUpDown size={12} />
                </button>
              </th>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">
                <button
                  className="inline-flex items-center gap-1 hover:text-[#1A1A1A]"
                  onClick={() => setSort("date")}
                  data-testid="sort-date"
                >
                  Event date <ArrowUpDown size={12} />
                </button>
              </th>
              <th className="px-3 py-2">
                <button
                  className="inline-flex items-center gap-1 hover:text-[#1A1A1A]"
                  onClick={() => setSort("days")}
                  data-testid="sort-days"
                >
                  Days until <ArrowUpDown size={12} />
                </button>
              </th>
              <th className="px-3 py-2">
                <button
                  className="inline-flex items-center gap-1 hover:text-[#1A1A1A]"
                  onClick={() => setSort("status")}
                  data-testid="sort-status"
                >
                  Status <ArrowUpDown size={12} />
                </button>
              </th>
              <th className="px-3 py-2">Last modified</th>
              <th className="px-3 py-2 text-right">Preview</th>
            </tr>
          </thead>
          <tbody className="text-[#1A1A1A]">
            {sorted.map((row) => (
              <tr
                key={row.slug}
                className="border-t border-[#E5E7EB]"
                data-testid={`marquee-row-${row.slug}`}
              >
                <td className="px-3 py-2 font-mono text-xs text-[#374151]">
                  {row.slug}
                </td>
                <td className="px-3 py-2 capitalize">{row.islandName}</td>
                <td className="px-3 py-2">{row.eventLabel}</td>
                <td className="px-3 py-2 text-[#374151]">
                  {row.date ? (
                    <>
                      {row.date}
                      {row.time && (
                        <span className="text-xs text-[#6B7280] ml-1">
                          {row.time}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-[#6B7280] italic">series (no date)</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${daysBadgeStyle(
                      row.days,
                      row.status
                    )}`}
                    data-testid={`freshness-days-${row.slug}`}
                  >
                    {row.days === null
                      ? "n/a"
                      : row.days < 0
                        ? `${Math.abs(row.days)}d ago`
                        : `${row.days}d`}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border capitalize ${statusBadgeStyle(
                      row.status
                    )}`}
                    data-testid={`freshness-status-${row.slug}`}
                  >
                    {row.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-[#374151]">
                  {row.dateModified}
                  {row.dateModifiedIsPerEvent ? (
                    <span
                      className="ml-1 text-[10px] uppercase tracking-wider text-[#0AADA0]"
                      title="Per-event dateModified stamp"
                      data-testid={`freshness-per-event-${row.slug}`}
                    >
                      per-event
                    </span>
                  ) : (
                    <span
                      className="ml-1 text-[10px] uppercase tracking-wider text-[#6B7280]"
                      title="Falls back to CARNIVAL_LAST_UPDATED (no per-event stamp yet)"
                    >
                      shared
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <Link
                    to={`/carnival/${row.slug}`}
                    className="inline-flex items-center gap-1 text-[#0AADA0] hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`marquee-preview-${row.slug}`}
                  >
                    Open <ExternalLink size={12} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[#6B7280]">
        Green Days-Until badge = event is confirmed. Amber = estimated event
        within 30 days (highest priority for copy refresh once organizers
        publish their firm schedule). Yellow = estimated within 90 days. Blue
        = estimated, more than 90 days out.
      </p>
    </div>
  );
};
